import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, Text } from '@bonusly/design-system';

import type { BizyUiBlockAction, RecognitionViewCard } from '../../../../lib/bizy/types/ui-blocks';
import { useBizyChatActions, useBizyUiBlockActionDispatcher } from '../context/actions-context';
import { AmountPill, HashtagToken, RecipientHandle } from '../lib/recognition-segments';
import type { BizyNodeProps } from './node-props';

/**
 * Read-only reference to a real recognition post. The full Bonusly post header
 * is shown because this is a snapshot of a real bonus that already exists.
 */
export function RecognitionViewCardNode({ node }: BizyNodeProps) {
	const block = node as RecognitionViewCard;
	const { fontSize = 'sm', isReadOnly } = useBizyChatActions();
	const { giver, recipient, amount, message, hashtags, date, actions } = block.payload;
	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const hashtagList = (hashtags ?? []).map((h) => h.replace(/^#/, ''));
	const hasHeader = !!giver || recipients.length > 0 || (amount != null && amount > 0);
	const hasFooter = !!date || actions.length > 0;

	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			{hasHeader && (
				<Text fontSize={fontSize} lineHeight="tall">
					{giver && (
						<Text as="span" fontSize="inherit" fontWeight="semibold">
							{giver}:{' '}
						</Text>
					)}
					{recipients.map((r, i) => (
						<span key={`${r}-${i}`}>
							<RecipientHandle username={r} />{' '}
						</span>
					))}
					{amount != null && amount > 0 && <AmountPill amount={amount} />}
				</Text>
			)}
			<Text fontSize={fontSize} mt={hasHeader ? 1 : 0} whiteSpace="pre-wrap">
				{message}
				{hashtagList.length > 0 && (
					<>
						{' '}
						{hashtagList.map((h, i) => (
							<span key={`${h}-${i}`}>
								<HashtagToken hashtag={h} />
								{i < hashtagList.length - 1 ? ' ' : ''}
							</span>
						))}
					</>
				)}
			</Text>
			{hasFooter && (
				<Flex justify="space-between" align="center" mt={3}>
					<Text fontSize="xs" color="text.softer">
						{date}
					</Text>
					<Flex gap={2} align="center">
						{actions.map((action, index) => (
							<InlineFooterAction key={`${action.type}-${index}`} action={action} isDisabled={isReadOnly} />
						))}
					</Flex>
				</Flex>
			)}
		</Box>
	);
}

/** Local single-action button for the footer row. */
function InlineFooterAction({ action, isDisabled }: { action: BizyUiBlockAction; isDisabled: boolean }) {
	const { t } = useTranslation('admin_bizy');
	const dispatch = useBizyUiBlockActionDispatcher();
	const label =
		action.label ??
		(action.type === 'view_post'
			? t('uiBlocks.actions.viewPost')
			: action.type === 'open_in_givebox'
				? t('uiBlocks.actions.openInGivebox')
				: action.type === 'dismiss'
					? t('uiBlocks.actions.dismiss')
					: action.type === 'insert_into_draft'
						? t('uiBlocks.actions.insertIntoDraft')
						: t('uiBlocks.actions.sendNow'));
	return (
		<Button variant="ghost" colorScheme="secondary" size="sm" onClick={() => dispatch(action)} isDisabled={isDisabled}>
			{label}
		</Button>
	);
}
